import { Inject, Injectable } from '@angular/core';

import { OddsAcceptanceMode } from '@bpos';
import { ScrollFreezeService } from '@frontend/sports/common/core/feature/scroll-freeze';
import { DeviceService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import Decimal from 'decimal.js';
import { keyBy } from 'lodash-es';
import { NumpadValidationParams } from 'packages/sports/web/app/src/numpad/model';
import { NUMPAD_VALIDATION_PARAMETERS_TOKEN } from 'packages/sports/web/app/src/numpad/numpad-validation-parameters';

import { IBetslipState } from '../../../base/store/state';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickId } from '../../../core/picks/pick-id';
import { PickOddsState } from '../../../core/picks/pick-models';
import { IBetslip } from '../../bet-generation/models';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import { RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { getSelectedTokenForContext } from '../../reward-tokens/services/linear-reward-tokens.utils';
import { isFreebetToken } from '../../reward-tokens/services/reward-tokens.utils';
import { ISingleBetState } from '../../single-bet/state';
import { IBetslipSummary } from '../../summary/models';
import { getOddsBoostWinningsBoost } from '../../summary/utils/betslip-summary.utils';
import { IBetslipTypeState } from '../../types/state';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { MinSelectionsBetbuilderError } from '../../validation/errors/general/min-selections-betbuilder-error';
import {
    getClosedLegIdsWithinValidGroups,
    getSuspendedLegIdsWithinValidGroups,
    isNotDividableStakeErrorPreCheck,
    isRewardTokensMaxStakeError,
    isRewardTokensSlipTypeError,
} from '../../validation/services/utils/betslip-errors-utils';
import { OddsChangedHelperService } from '../../validation/services/validators/picks/odds-changed/odds-changed-helper.service';
import { IBetslipPickErrors } from '../../validation/state';
import { QuickBetActions } from '../quick-bet-actions';
import {
    IQuickBetActionButtonState,
    IQuickBetErrorsStatus,
    IQuickBetStakeState,
    IQuickBetWinningsState,
    QuickBetActionButtonState,
} from '../quick-bet.state';

export function findQuickBetPicks(pickList: BetslipPick[], quickBetPickIds: PickId[]): BetslipPick[] {
    const stringPickIds = keyBy(quickBetPickIds, (pickId) => pickId.toString());

    return pickList.filter((pick) => stringPickIds[pick.id.toString()]);
}

@Injectable()
export class QuickBetUtils {
    isUserOnPlayBreak: boolean;

    constructor(
        @Inject(NUMPAD_VALIDATION_PARAMETERS_TOKEN) private validationParameters: NumpadValidationParams,
        private scrollFreeze: ScrollFreezeService,
        private deviceService: DeviceService,
        private store: Store,
        private oddsChangedHelperService: OddsChangedHelperService,
        private betslipPickUtils: BetslipPickUtilsService,
    ) {}

    getStakeState(singleBetState: ISingleBetState, stakeValue?: string): IQuickBetStakeState {
        const picks = Object.values(singleBetState.picks);
        const pick = picks && picks[0];
        const stake = stakeValue || pick?.stake?.toString();

        const stakeState = <IQuickBetStakeState>{
            stake,
        };

        return stakeState;
    }

    getWinningsState(summaryData: [IBetslip, IBetslipSummary]): IQuickBetWinningsState {
        const summary = summaryData[1];

        const winningsBoost = getOddsBoostWinningsBoost(summary);
        const accaBoostModifications = summary.slipSummaries[0]?.rewardTokenModifications?.accaBoost;
        const riskFreeAmount = summary.slipSummaries.some((slipSummary) => !!slipSummary.rewardTokenModifications?.riskFree)
            ? summary.slipSummaries.reduce(
                  (totalRiskFreeAmount, slipSummary) => totalRiskFreeAmount.plus(slipSummary.rewardTokenModifications?.riskFree?.amount || 0),
                  new Decimal(0),
              )
            : null;

        const taxationLiability = null;
        const hasTaxation = summary.taxation && (summary.taxation?.amount?.greaterThan(0) || summary.taxation?.isPlaceholderTax) ? true : false;

        return {
            hasTaxation,
            possibleWinnings: summary.possibleWinnings,
            winningsBoost,
            riskFreeAmount,
            taxationAmount: summary.taxation?.amount || null,
            taxationLiability,
            taxationRate: summary.taxation?.rate || null,
            possibleWinningsNet: summary.possibleWinningsNet,
            originalWinnings: accaBoostModifications?.originalWinnings ?? null,
        };
    }

    getActionButtonState(
        summaryData: [IBetslip, IBetslipSummary],
        betslipState: IBetslipState,
        errorsStatus: IQuickBetErrorsStatus,
        rewardTokenContext: RewardTokenContext,
    ): IQuickBetActionButtonState {
        const quickBetState = betslipState.quickBet;
        const buttonState = betslipState.quickBet.actionButtonState;
        const state = buttonState.state;

        const token = getSelectedTokenForContext(rewardTokenContext, betslipState.rewardTokens.tokens, betslipState.types);
        const isFreeBetSelected = !!token && isFreebetToken(token);

        let isDisabled: boolean;
        if (
            state === QuickBetActionButtonState.Login ||
            state === QuickBetActionButtonState.MakeDeposit ||
            state === QuickBetActionButtonState.UpdateMinStake ||
            (state === QuickBetActionButtonState.AcceptChanges && (quickBetState.hasClosedPick || quickBetState.hasLockedPick))
        ) {
            isDisabled = false;
        } else if (betslipState.quickDeposit.isOpen) {
            isDisabled = !betslipState.quickDeposit.isAvailable || buttonState.isDisabled;
        } else {
            isDisabled =
                buttonState.isDisabled ||
                errorsStatus.hasBetslipErrors ||
                betslipState.quickBet.betPlacedResult.warnings.some((w) => w.disableButton) ||
                (!Number(betslipState.quickBet.stakeState.stake) && !isFreeBetSelected);
        }

        const winningsState = this.getWinningsState(summaryData);

        return {
            state,
            isDisabled,
            isProcessing: buttonState.isProcessing,
            hasTaxation: winningsState.hasTaxation,
            possibleWinnings: winningsState.possibleWinnings,
            winningsBoost: winningsState.winningsBoost,
            riskFreeAmount: winningsState.riskFreeAmount,
            taxationAmount: winningsState.taxationAmount,
            taxationLiability: winningsState.taxationLiability,
            possibleWinningsNet: winningsState.possibleWinningsNet,
        };
    }

    tryFreezeScroll(): void {
        if (this.isiOS()) {
            return;
        }
        this.freezeScroll();
    }

    freezeScroll(): void {
        if (!this.scrollFreeze.isScrollFrozen) {
            this.scrollFreeze.freezeScroll();
        }
    }

    tryReleaseScroll(): void {
        if (this.scrollFreeze.isScrollFrozen) {
            this.scrollFreeze.releaseScroll();
        }
    }

    isiOS(): boolean {
        return this.deviceService.isiOS;
    }

    hideError(errors: BetslipError[], quickBetStake: string | null): boolean {
        if (!errors.length) {
            return true;
        }

        const notDividableError = errors.find(isNotDividableStakeErrorPreCheck);
        if (notDividableError) {
            return this.hideDividableError(quickBetStake || '0', notDividableError.divident);
        }

        const rewardTokensSlipTypeError = errors.find(isRewardTokensSlipTypeError);

        if (rewardTokensSlipTypeError) {
            return true;
        }

        const rewardTokensMaxStakeError = errors.find(isRewardTokensMaxStakeError);

        if (rewardTokensMaxStakeError) {
            return true;
        }

        const minSelectionsBetbuilderError = errors.find((e) => e instanceof MinSelectionsBetbuilderError);

        return !!minSelectionsBetbuilderError;
    }

    cleanBetslipErrors(): void {
        this.cleanErrors();
        this.clearWarnings();
    }

    areOddsAccepted(pickList: BetslipPick[], pickIds: PickId[], oddsAcceptance: OddsAcceptanceMode): boolean {
        const picks = findQuickBetPicks(pickList, pickIds);

        return this.oddsChangedHelperService.areAllPickOddsAccepted(picks, oddsAcceptance);
    }

    getClosedAndLockedPickCount(
        type: IBetslipTypeState,
        pickList: BetslipPick[],
        comboPickErrors: IBetslipPickErrors,
    ): { closedPickCount: number; lockedPickCount: number } {
        const selectedPicks = this.betslipPickUtils.getSelectedPicks(type, pickList);
        const closedPickCount =
            selectedPicks.filter((p) => p.currentPrice && p.oddsState === PickOddsState.Closed).length +
            getClosedLegIdsWithinValidGroups(selectedPicks, comboPickErrors).length;

        const lockedPickCount =
            selectedPicks.filter((p) => p.currentPrice && p.oddsState === PickOddsState.Locked).length +
            getSuspendedLegIdsWithinValidGroups(selectedPicks, comboPickErrors).length;

        return { closedPickCount, lockedPickCount };
    }

    private clearWarnings(): void {
        this.store.dispatch(QuickBetActions.clearWarnings());
    }

    private cleanErrors(): void {
        this.store.dispatch(BetslipErrorsActions.cleanManualRemoveErrors());
    }

    /**
     * Check if a valid stake can still be reached for dividable Errors
     *
     * @param stake Stake
     * @param unit Divisor
     */
    private hideDividableError(stake: string, unit: Decimal): boolean {
        // Check only after separator has been typed for performance
        if (stake.indexOf('.') < 0) {
            return true;
        }

        const currentStake = new Decimal(stake);

        // rest from dividing
        // eg. ({stake: '1.', divident: 0.3} -> 0.1)
        const congruent = currentStake.modulo(unit);
        // necessary difference to reach valid stake
        // eg. ({stake: '1.' congruent: 0.1, divident: 0.3} -> 0.2) as 1.2 is nearest valid stake
        const differenceToAdd = unit.minus(congruent);

        // possible increase that could be reached by further typing
        // => use highest digit (in base-10) on each remaining empty positional notation and extract the current value
        // e.g. ({stake: '1.'} -> 0.99), ({stake: 1.1} -> 0.09)
        const possibleIncrease = new Decimal(`${stake}${'9'.repeat(this.validationParameters.maxTrailingDigits - stake.split('.')[1].length)}`).minus(
            currentStake,
        );

        return possibleIncrease > differenceToAdd;
    }
}
